import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Zap, AlertTriangle } from "lucide-react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ReoProfile } from "@/components/realtor/ReoProfile";
import { ReoSites } from "@/components/realtor/ReoSites";

const Stat = ({ k, label, v }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2" data-testid={`reo-stat-${k}`}><div className="text-xs text-slate-500">{label}</div><div className="font-display text-xl font-bold text-slate-900">{v}</div></div>
);

const ReoProgress = ({ d, act }) => {
  const p = d.progress;
  return (
    <Card className="mb-5 p-6" data-testid="reo-progress-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-lg font-bold text-slate-900">Application progress</h3>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Keep applying automatically
            <Switch checked={!!d.profile.autopilot} onCheckedChange={(v) => act(() => api.put("/rt/reo/autopilot", { on: v }), v ? "Autopilot on - Ava keeps applying" : "Autopilot off")} data-testid="reo-autopilot-switch" /></label>
          <Button className="gold-cta border-0 font-bold" onClick={() => act(() => api.post("/rt/reo/apply-now"), "Ava is applying now")} data-testid="reo-apply-now"><Zap className="mr-2 h-4 w-4" />Apply to all now</Button>
        </div>
      </div>
      <Progress value={p.total ? (p.applied / p.total) * 100 : 0} className="h-3" data-testid="reo-progress-bar" />
      <div className="mt-1 text-xs text-slate-500" data-testid="reo-progress-label">{p.applied} of {p.total} websites applied · {p.approved} approved</div>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat k="total" label="Websites" v={p.total} /><Stat k="applying" label="Applying / queued" v={p.applying + p.queued} /><Stat k="applied" label="Applied" v={p.applied} />
        <Stat k="approved" label="Approved" v={p.approved} /><Stat k="attention" label="Need attention" v={p.failed + p.payment_due} />
      </div>
    </Card>
  );
};

export default function RtReo() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/rt/reo").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { if (user?.role !== "property_manager") { load(); const t = setInterval(load, 20000); return () => clearInterval(t); } return undefined; }, [load, user]);
  const act = async (fn, msg) => {
    try { const r = await fn(); setD(r.data); if (msg) toast.success(msg); return true; } catch (e) { toast.error(apiError(e)); return false; }
  };
  if (user?.role === "property_manager") return <Navigate to="/pm" replace />;
  if (!d) return null;
  return (
    <div className="mx-auto max-w-6xl" data-testid="rt-reo-page">
      <h1 className="font-display text-3xl font-extrabold text-slate-900">REO</h1>
      <p className="mb-5 text-sm text-slate-600">Ava applies to bank-owned (REO) listing programs for you. If an application fails, she emails you, and you can reply to tell her what to do.</p>
      {!d.browser_use_ready && <div className="mb-4 flex gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="reo-not-ready"><AlertTriangle className="h-4 w-4 flex-none" />REO applications aren't connected yet. The administrator is finishing the setup.</div>}
      <ReoProgress d={d} act={act} />
      <ReoProfile d={d} act={act} />
      <ReoSites d={d} act={act} />
    </div>
  );
}
